import { AgentEntry, type AgentView } from "@dramapie/shared";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from "react";

import {
  abortAgent,
  getAgentView,
  sendAgentMessage,
  uploadProjectAttachment,
} from "../lib/api";
import {
  bindTopicSession,
  createTopic,
  DEFAULT_TOPIC_TITLE,
  formatTopicTime,
  loadActiveTopicId,
  loadTopics,
  removeTopic,
  renameTopic,
  resolveActiveTopic,
  saveActiveTopicId,
  saveTopics,
  sortTopics,
  topicTitleFromText,
  touchTopic,
  type AgentTopic,
} from "../lib/agent-topics";
import { errorMessage } from "../lib/errors";
import { formatPoints } from "../lib/format";
import { STREAM_STATUS_LABEL, type StreamHub, type StreamMessage, type StreamStatus } from "../lib/stream";
import { EmptyState, ErrorState, LoadingState } from "./states";

type LoadState = "loading" | "ready" | "error";
type AttachmentKind = "image" | "text" | "audio" | "video";

interface PendingAttachment {
  path: string;
  kind: AttachmentKind;
  name: string;
}

const TOOL_STATUS_META: Record<
  NonNullable<AgentEntry["status"]>,
  { label: string; className: string }
> = {
  running: { label: "运行中", className: "is-running" },
  ok: { label: "完成", className: "is-ok" },
  error: { label: "失败", className: "is-error" },
};

function kindForFile(contentType: string): AttachmentKind {
  if (contentType.startsWith("image/")) return "image";
  if (contentType.startsWith("audio/")) return "audio";
  if (contentType.startsWith("video/")) return "video";
  return "text";
}

function summarizeArgs(args: Record<string, unknown> | undefined): string {
  if (!args) return "无参数";
  let json: string;
  try {
    json = JSON.stringify(args);
  } catch {
    return "参数无法序列化";
  }
  return json.length > 60 ? `${json.slice(0, 60)}…` : json;
}

/** 把流式条目合并进列表：同 id 覆盖，user 消息到达时清掉本地乐观气泡。 */
function upsertEntry(prev: AgentEntry[], entry: AgentEntry): AgentEntry[] {
  const index = prev.findIndex((item) => item.id === entry.id);
  let next =
    index === -1
      ? [...prev, entry]
      : prev.map((item, itemIndex) =>
          itemIndex === index ? { ...item, ...entry } : item,
        );

  if (entry.kind === "user") {
    const localIndex = next.findIndex(
      (item) => item.id.startsWith("local_") && item.kind === "user" && item.text === entry.text,
    );
    if (localIndex !== -1) next = next.filter((_, itemIndex) => itemIndex !== localIndex);
  }
  return next;
}

function appendDelta(prev: AgentEntry[], entryId: string, text: string): AgentEntry[] {
  const index = prev.findIndex((entry) => entry.id === entryId);
  if (index === -1) {
    return [...prev, { id: entryId, kind: "assistant", text, at: Date.now() }];
  }
  return prev.map((entry, itemIndex) =>
    itemIndex === index ? { ...entry, text: entry.text + text } : entry,
  );
}

function ToolEntry({ entry }: { entry: AgentEntry }) {
  const [expanded, setExpanded] = useState(false);
  const status = entry.status ? TOOL_STATUS_META[entry.status] : null;
  const thinking =
    entry.args && typeof entry.args.thinking === "string" ? entry.args.thinking : null;
  const summary = summarizeArgs(entry.args);

  return (
    <li className="dp-tool">
      <button
        type="button"
        className="dp-tool-toggle"
        aria-expanded={expanded}
        onClick={() => setExpanded((value) => !value)}
      >
        <span className="dp-tool-caret" aria-hidden="true">
          {expanded ? "▾" : "▸"}
        </span>
        <span className="dp-tool-name">{entry.name ?? "工具调用"}</span>
        {status ? (
          <span className={`dp-tool-status ${status.className}`}>{status.label}</span>
        ) : null}
        <span className="truncate">参数摘要 {summary}</span>
      </button>

      {expanded ? (
        <div className="dp-tool-body">
          {thinking ? <p>{thinking}</p> : null}
          <pre>{JSON.stringify(entry.args ?? {}, null, 2)}</pre>
        </div>
      ) : null}

      {entry.text && !expanded ? <p className="dp-msg-text">{entry.text}</p> : null}
    </li>
  );
}

function EntryItem({ entry }: { entry: AgentEntry }) {
  if (entry.kind === "tool") return <ToolEntry entry={entry} />;

  if (entry.kind === "user") {
    return (
      <li className="dp-msg user">
        <div className="dp-msg-text">{entry.text}</div>
      </li>
    );
  }

  if (entry.kind === "system") {
    return (
      <li className="dp-msg system">
        <div className="dp-msg-text">{entry.text}</div>
      </li>
    );
  }

  return (
    <li className="dp-msg">
      <div className="dp-msg-text">{entry.text}</div>
    </li>
  );
}

export interface AgentPanelProps {
  projectId: string;
  /** WorkspacePage 的 WebSocket 扇出中心；缺省时面板只靠 HTTP。 */
  events?: StreamHub;
  streamStatus?: StreamStatus;
  /** 快捷提问：点了只填进输入框，不自动发送。 */
  quickPrompts?: string[];
  /** 执行状态上报（顶栏入口的小圆点靠它）。 */
  onBusyChange?: (busy: boolean) => void;
}

/**
 * Agent 会话（Wiki A-01~A-07）。
 *
 * 头部只有「Agent」+ 新建话题 + 话题列表（原型 `agentPane` 2179 的骨架，
 * 按用户反馈去掉「导演助理」角色名 / 头像 / 「空闲」这类无意义状态）；
 * 消息流来自 `GET .../agent/view` 快照，之后由 WS 的
 * `agent.delta` / `agent.entry` / `agent.done` 增量更新。
 *
 * 话题表是本地 state（后端目前一个项目一个 session），见 `lib/agent-topics.ts`。
 */
export function AgentPanel({
  projectId,
  events,
  streamStatus = "idle",
  quickPrompts,
  onBusyChange,
}: AgentPanelProps) {
  const [view, setView] = useState<AgentView | null>(null);
  const [entries, setEntries] = useState<AgentEntry[]>([]);
  const [busy, setBusy] = useState(false);
  const [loadState, setLoadState] = useState<LoadState>("loading");
  const [loadError, setLoadError] = useState("");
  const [reloadToken, setReloadToken] = useState(0);

  const [draft, setDraft] = useState("");
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [aborting, setAborting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // 话题：本地表 + 每个话题一份消息缓存（切回来不丢）
  const [topics, setTopics] = useState<AgentTopic[]>([]);
  const [activeTopicId, setActiveTopicId] = useState("");
  const [topicsOpen, setTopicsOpen] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState("");
  const topicCache = useRef<Map<string, AgentEntry[]>>(new Map());
  const activeTopicRef = useRef("");
  const topicsWrapRef = useRef<HTMLDivElement | null>(null);
  const topicsButtonRef = useRef<HTMLButtonElement | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const localSeq = useRef(0);

  activeTopicRef.current = activeTopicId;

  // 项目切换：重建话题表（默认一个「新话题」，sessionId 等 /agent/view 回来再绑）。
  useEffect(() => {
    const stored = loadTopics(projectId);
    const initial = stored.length > 0 ? stored : [createTopic()];
    const active = resolveActiveTopic(initial, loadActiveTopicId(projectId)) ?? initial[0];
    topicCache.current = new Map();
    setTopics(initial);
    setActiveTopicId(active ? active.id : "");
    setTopicsOpen(false);
    setRenamingId(null);
  }, [projectId]);

  // 话题表持久化：刷新页面还认得自己开过哪些话题。
  useEffect(() => {
    if (topics.length === 0) return;
    saveTopics(projectId, topics);
  }, [projectId, topics]);

  useEffect(() => {
    if (!activeTopicId) return;
    saveActiveTopicId(projectId, activeTopicId);
  }, [projectId, activeTopicId]);

  useEffect(() => {
    const controller = new AbortController();
    setLoadState("loading");
    setLoadError("");
    setActionError(null);
    setView(null);
    setEntries([]);
    setDraft("");
    setAttachments([]);

    getAgentView(projectId, { signal: controller.signal })
      .then((data) => {
        setView(data);
        setEntries(data.entries);
        setBusy(data.busy);
        setLoadState("ready");
        const topicId = activeTopicRef.current;
        if (topicId) {
          topicCache.current.set(topicId, data.entries);
          // 默认话题绑定后端真实会话（后端目前只有一条，见 lib/agent-topics.ts），
          // 标题用快照里的第一条用户消息，列表里才不是一排「新话题」。
          const firstUser = data.entries.find(
            (entry) => entry.kind === "user" && entry.text.trim().length > 0,
          );
          setTopics((prev) => {
            const bound = bindTopicSession(prev, topicId, data.sessionId);
            if (!firstUser) return bound;
            return bound.map((topic) =>
              topic.id === topicId && topic.title === DEFAULT_TOPIC_TITLE
                ? { ...topic, title: topicTitleFromText(firstUser.text) }
                : topic,
            );
          });
        }
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") return;
        setLoadState("error");
        setLoadError(errorMessage(error, "执行记录加载失败"));
      });

    return () => controller.abort();
  }, [projectId, reloadToken]);

  const handleEvent = useCallback((message: StreamMessage) => {
    if (message.type === "agent.delta") {
      const entryId = message.payload.entryId;
      const text = message.payload.text;
      if (typeof entryId !== "string" || typeof text !== "string" || text.length === 0) return;
      setEntries((prev) => appendDelta(prev, entryId, text));
      return;
    }

    if (message.type === "agent.entry") {
      const entry = AgentEntry.safeParse(message.payload.entry);
      if (!entry.success) return;
      setEntries((prev) => upsertEntry(prev, entry.data));
      return;
    }

    if (message.type === "agent.done") {
      setBusy(false);
      setView((prev) => (prev ? { ...prev, busy: false } : prev));
    }
  }, []);

  useEffect(() => {
    if (!events) return;
    return events.subscribe(handleEvent);
  }, [events, handleEvent]);

  // 让顶栏知道 Agent 是否在跑（不伪造「未读」，只报真实执行态）。
  useEffect(() => {
    onBusyChange?.(busy);
  }, [busy, onBusyChange]);

  // 话题菜单：Esc 关闭并把焦点还给入口，点外面也关。
  useEffect(() => {
    if (!topicsOpen) return;
    function handlePointerDown(event: MouseEvent) {
      if (!topicsWrapRef.current?.contains(event.target as Node)) setTopicsOpen(false);
    }
    function handleKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        setTopicsOpen(false);
        setRenamingId(null);
        topicsButtonRef.current?.focus();
        return;
      }
      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
      const items = Array.from(
        topicsWrapRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? [],
      );
      if (items.length === 0) return;
      event.preventDefault();
      const index = items.indexOf(document.activeElement as HTMLButtonElement);
      const step = event.key === "ArrowDown" ? 1 : -1;
      const next = index === -1 ? 0 : (index + step + items.length) % items.length;
      items[next]?.focus();
    }
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [topicsOpen]);

  const canSend = loadState === "ready" && !busy && !uploading && draft.trim().length > 0;
  const canAttach = loadState === "ready" && !busy && !uploading && attachments.length < 9;

  const activeTopic = resolveActiveTopic(topics, activeTopicId);
  const orderedTopics = sortTopics(topics);

  function stashActiveTopic() {
    if (activeTopicId) topicCache.current.set(activeTopicId, entries);
  }

  function switchTopic(id: string) {
    setTopicsOpen(false);
    setRenamingId(null);
    if (id === activeTopicId) return;
    stashActiveTopic();
    setActiveTopicId(id);
    setTopics((prev) => touchTopic(prev, id));
    setEntries(topicCache.current.get(id) ?? []);
    setActionError(null);
  }

  function createNewTopic() {
    stashActiveTopic();
    const topic = createTopic();
    setTopics((prev) => [topic, ...prev]);
    setActiveTopicId(topic.id);
    setEntries([]);
    setDraft("");
    setTopicsOpen(false);
    setRenamingId(null);
    inputRef.current?.focus();
  }

  function commitRename(event?: FormEvent) {
    event?.preventDefault();
    if (!renamingId) return;
    setTopics((prev) => renameTopic(prev, renamingId, renameDraft));
    setRenamingId(null);
  }

  function startRename(topic: AgentTopic) {
    setRenamingId(topic.id);
    setRenameDraft(topic.title);
  }

  function deleteTopic(id: string) {
    stashActiveTopic();
    topicCache.current.delete(id);
    const remaining = removeTopic(topics, id);
    const nextList = remaining.length > 0 ? remaining : [createTopic()];
    setTopics(nextList);
    if (id === activeTopicId) {
      const next = resolveActiveTopic(nextList, null);
      setActiveTopicId(next ? next.id : "");
      setEntries(next ? topicCache.current.get(next.id) ?? [] : []);
    }
    setRenamingId(null);
  }

  async function handleSend() {
    if (!canSend) return;
    const text = draft.trim();
    const outgoing = attachments.map(({ path, kind }) => ({ path, kind }));
    localSeq.current += 1;
    const localId = `local_${localSeq.current}`;
    const topicId = activeTopicId;

    setEntries((prev) => [...prev, { id: localId, kind: "user", text, at: Date.now() }]);
    setDraft("");
    setBusy(true);
    setActionError(null);
    // 话题标题跟着第一条消息走，最后活动时间刷新。
    setTopics((prev) =>
      prev.map((topic) =>
        topic.id === topicId
          ? {
              ...topic,
              lastActiveAt: Date.now(),
              title: topic.title === DEFAULT_TOPIC_TITLE ? topicTitleFromText(text) : topic.title,
            }
          : topic,
      ),
    );

    try {
      const output = await sendAgentMessage(projectId, { text, attachments: outgoing });
      setAttachments([]);
      // 后端多会话后这里就是话题自己的 sessionId；现在只用于对齐默认话题。
      setTopics((prev) => bindTopicSession(prev, topicId, output.sessionId));
    } catch (error) {
      setBusy(false);
      setEntries((prev) => prev.filter((entry) => entry.id !== localId));
      setDraft(text);
      setActionError(`发送失败：${errorMessage(error)}`);
    }
  }

  async function handleAbort() {
    if (!busy || aborting) return;
    setAborting(true);
    setActionError(null);
    try {
      await abortAgent(projectId);
      setBusy(false);
      setView((prev) => (prev ? { ...prev, busy: false } : prev));
    } catch (error) {
      setActionError(`中止失败：${errorMessage(error)}`);
    } finally {
      setAborting(false);
    }
  }

  async function handleFiles(fileList: FileList | null) {
    if (!fileList || fileList.length === 0 || !canAttach) return;
    setUploading(true);
    setActionError(null);
    const room = 9 - attachments.length;
    const files = Array.from(fileList).slice(0, room);

    try {
      const uploaded: PendingAttachment[] = [];
      for (const file of files) {
        const output = await uploadProjectAttachment(projectId, file);
        uploaded.push({
          path: output.path,
          kind: kindForFile(file.type),
          name: file.name,
        });
      }
      setAttachments((prev) => [...prev, ...uploaded].slice(0, 9));
    } catch (error) {
      setActionError(`上传失败：${errorMessage(error)}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key !== "Enter" || event.shiftKey) return;
    if (event.nativeEvent.isComposing) return;
    event.preventDefault();
    if (canSend) void handleSend();
  }

  function usePrompt(prompt: string) {
    setDraft(prompt);
    inputRef.current?.focus();
  }

  const usage = view?.usage;

  return (
    <div className="dp-agent">
      <div className="dp-agent-head">
        <h2 className="dp-agent-title">Agent</h2>
        {busy ? (
          <span className="dp-agent-busy" role="status">
            执行中…
          </span>
        ) : null}
        <span className="grow" />
        <button
          type="button"
          className="dp-btn sm dp-topic-new"
          onClick={createNewTopic}
          title="新建话题（本地记录，后端多会话接口待补）"
        >
          ＋ 新建话题
        </button>
        <div className="dp-topic-wrap" ref={topicsWrapRef}>
          <button
            ref={topicsButtonRef}
            type="button"
            className="dp-icon-btn dp-topic-trigger"
            aria-haspopup="menu"
            aria-expanded={topicsOpen}
            aria-label="话题列表"
            title={`话题列表（${topics.length}）· ${
              activeTopic ? activeTopic.title : "无"
            } · ${STREAM_STATUS_LABEL[streamStatus]}`}
            onClick={() => setTopicsOpen((value) => !value)}
          >
            ☰
          </button>
          {topicsOpen ? (
            <div className="dp-topic-menu" role="menu" aria-label="话题列表">
              <div className="dp-topic-menu-head">
                <span>话题 · {topics.length}</span>
                <span>最后活动</span>
              </div>
              {orderedTopics.map((topic) => (
                <div
                  key={topic.id}
                  className={`dp-topic-row${topic.id === activeTopicId ? " is-active" : ""}`}
                >
                  {renamingId === topic.id ? (
                    <form className="dp-topic-rename" onSubmit={(event) => commitRename(event)}>
                      <input
                        aria-label={`重命名话题 ${topic.title}`}
                        value={renameDraft}
                        onChange={(event) => setRenameDraft(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key !== "Escape") return;
                          event.preventDefault();
                          setRenamingId(null);
                        }}
                        autoFocus
                      />
                      <button type="submit" className="dp-btn sm">
                        保存
                      </button>
                    </form>
                  ) : (
                    <button
                      type="button"
                      role="menuitem"
                      className="dp-topic-switch"
                      aria-current={topic.id === activeTopicId ? "true" : undefined}
                      onClick={() => switchTopic(topic.id)}
                    >
                      <span className="dp-topic-name">{topic.title}</span>
                      <span className="dp-topic-time">{formatTopicTime(topic.lastActiveAt)}</span>
                    </button>
                  )}
                  <button
                    type="button"
                    className="dp-icon-btn dp-topic-act"
                    aria-label={`重命名话题 ${topic.title}`}
                    title="重命名"
                    onClick={() => startRename(topic)}
                  >
                    ✎
                  </button>
                  <button
                    type="button"
                    className="dp-icon-btn dp-topic-act"
                    aria-label={`删除话题 ${topic.title}`}
                    title="删除"
                    onClick={() => deleteTopic(topic.id)}
                  >
                    ×
                  </button>
                </div>
              ))}
              <div className="dp-topic-menu-foot">
                <span className="dp-tiny-note">
                  后端暂只有一条会话；新话题先记在本地（待 `agent/sessions` 接口）。
                </span>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div role="log" aria-label="执行记录" aria-live="polite" className="dp-agent-log dp-scroll">
        {loadState === "loading" ? <LoadingState label="正在加载执行记录…" /> : null}

        {loadState === "error" ? (
          <ErrorState
            title="执行记录加载失败"
            message={loadError}
            onRetry={() => setReloadToken((token) => token + 1)}
            retryLabel="重新加载"
          />
        ) : null}

        {loadState === "ready" && entries.length === 0 ? (
          <EmptyState
            title="还没有执行记录"
            description="发一条消息，Agent 的思考、工具调用和结果会实时出现在这里。"
          />
        ) : null}

        {loadState === "ready" && entries.length > 0 ? (
          <ol className="dp-agent-list">
            {entries.map((entry) => (
              <EntryItem key={entry.id} entry={entry} />
            ))}
            {busy ? (
              <li className="dp-msg-typing">
                <i />
                <i />
                <i />
                <span>正在生成…</span>
              </li>
            ) : null}
          </ol>
        ) : null}
      </div>

      {quickPrompts && quickPrompts.length > 0 ? (
        <div className="dp-quick-row">
          {quickPrompts.map((prompt) => (
            <button
              key={prompt}
              type="button"
              className="dp-quick-chip"
              disabled={loadState !== "ready" || busy}
              onClick={() => usePrompt(prompt)}
            >
              {prompt}
            </button>
          ))}
        </div>
      ) : null}

      {usage ? (
        <p className="dp-agent-usage">
          输入 {formatPoints(usage.inputTokens)} · 输出 {formatPoints(usage.outputTokens)} ·{" "}
          {formatPoints(usage.points)} 点
        </p>
      ) : null}

      <div className="dp-agent-input">
        {attachments.length > 0 ? (
          <ul aria-label="待发送附件" className="dp-attach-row">
            {attachments.map((attachment) => (
              <li key={attachment.path} className="dp-attach">
                <span aria-hidden="true">{attachment.kind === "image" ? "🖼" : "📎"}</span>
                <span className="truncate">{attachment.name}</span>
                <button
                  type="button"
                  aria-label={`移除附件 ${attachment.name}`}
                  onClick={() =>
                    setAttachments((prev) => prev.filter((item) => item.path !== attachment.path))
                  }
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        ) : null}

        <div className="dp-agent-box">
          <label htmlFor="agent-input" className="dp-sr">
            给 Agent 的消息
          </label>
          <textarea
            id="agent-input"
            ref={inputRef}
            aria-label="给 Agent 的消息"
            rows={2}
            value={draft}
            disabled={loadState !== "ready" || busy}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={busy ? "Agent 正在执行，可中止…" : "问它关于这一集的问题，或让它改一镜…"}
          />

          {actionError ? (
            <p role="alert" className="dp-agent-error">
              {actionError}
            </p>
          ) : null}

          <div className="dp-agent-row">
            <label
              className={`dp-file-label${canAttach ? "" : " is-disabled"}`}
              title="添加附件"
            >
              <span aria-hidden="true">📎</span>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,text/*,audio/*,video/*"
                aria-label="添加附件"
                disabled={!canAttach}
                onChange={(event) => void handleFiles(event.target.files)}
                className="dp-sr"
              />
            </label>
            <span className="dp-tiny-note">
              {uploading ? "上传中…" : "Enter 发送 · Shift+Enter 换行"}
            </span>
            <span className="grow" />
            {busy ? (
              <button
                type="button"
                onClick={() => void handleAbort()}
                disabled={aborting}
                className="dp-btn sm danger"
              >
                {aborting ? "中止中…" : "中止"}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => void handleSend()}
              disabled={!canSend}
              aria-label="发送"
              className="dp-send"
            >
              <span aria-hidden="true">↗</span>
            </button>
          </div>
        </div>
        <p className="dp-agent-foot">
          消息会写回故事板；每次改动都有 rev，冲突时以服务端为准。
        </p>
      </div>
    </div>
  );
}
